/** The tag-category rows' rules (tour plan §2 S1 "Tag categories"; operator-defaults
 *  plan §A1 "Rules" R1–R6, L1), pure — the tour editor and the associations table share
 *  them:
 *
 *  - `flagChanges` — `applyDeps` as one multi-path change: linking tags to GPS devices /
 *    users turns location sharing on and guide linking off (R1); linking guides turns user
 *    linking off (R2); turning clean-up off turns "remove guides on clean-up" off (R4);
 *    turning guide linking off — directly or through R1 — turns "remove guides no tag
 *    refers to" off (R5).
 *  - `flagLock` — a flag that cannot change on its own right now, and why: location
 *    sharing while user linking is on (R3); "remove guides on clean-up" without clean-up
 *    (R4); "remove guides no tag refers to" without guide linking (R5); check-in on a row
 *    used for boarding and boarding on a row used for check-in (R6). A lock only stops a
 *    flag being turned ON (or, R3, off): a row stored against a rule can always be put
 *    right.
 *  - `effectiveCategories` / `limitProblems` / `limitLock` — at most 3 check-in categories
 *    and 1 boarding category among the categories the tour ends up with: its own rows, plus
 *    the operator's defaults it inherits (`use_operator_defaults`) where no own row
 *    overrides the same category. Given the stored set, `limitLock` applies the server's
 *    no-worse rule: a stored breach may persist (a check-in swapped for another), not grow. */

export interface FlagRow {
  /** The row's current value of a flag (`checkin`, `boarding`, …). */
  flag: (name: string) => boolean;
}

/** One flag edit, with what it sets or clears along with it. */
export function flagChanges(flag: string, to: boolean, row: FlagRow): Record<string, boolean> {
  const out: Record<string, boolean> = { [flag]: to };
  if (flag === 'user_association' && to) {
    out.location_sharing = true;
    out.guides_from_tags = false;
  }
  if (flag === 'guides_from_tags' && to && row.flag('user_association')) out.user_association = false;
  if (flag === 'cleanup_on_assignment' && !to && row.flag('remove_guides_on_cleanup')) out.remove_guides_on_cleanup = false;
  // R5 — only when guide linking really goes off: a row stored with "remove unreferenced"
  // but no guide linking is not rewritten by an unrelated edit.
  if (out.guides_from_tags === false && row.flag('guides_from_tags') && row.flag('remove_unreferenced_guides')) {
    out.remove_unreferenced_guides = false;
  }
  return out;
}

/** Why a flag cannot change on its own (null: it can). */
export function flagLock(flag: string, row: FlagRow): string | null {
  if (flag === 'location_sharing' && row.flag('user_association') && row.flag('location_sharing')) {
    return 'Required while tags link to GPS devices / users';
  }
  if (flag === 'remove_guides_on_cleanup' && !row.flag('cleanup_on_assignment') && !row.flag('remove_guides_on_cleanup')) {
    return 'Needs “Clean up on each assignment”';
  }
  if (flag === 'remove_unreferenced_guides' && !row.flag('guides_from_tags') && !row.flag('remove_unreferenced_guides')) {
    return 'Needs “Guides”';
  }
  if (flag === 'checkin' && !row.flag('checkin') && row.flag('boarding')) return 'Used for boarding — clear Boarding first';
  if (flag === 'boarding' && !row.flag('boarding') && row.flag('checkin')) return 'Used for check-in — clear Check-in first';
  return null;
}

export interface CategoryRow {
  category: number | null;
  checkin: boolean;
  boarding: boolean;
}

/** The categories the tour ends up with: own rows, plus inherited ones no own row
 *  overrides (only while it uses the operator defaults). */
export function effectiveCategories(own: CategoryRow[], inherited: CategoryRow[], useDefaults: boolean): CategoryRow[] {
  if (!useDefaults) return own;
  const mine = new Set(own.map((r) => r.category));
  return [...own, ...inherited.filter((r) => !mine.has(r.category))];
}

export const MAX_CHECKIN = 3;
export const MAX_BOARDING = 1;

/** The limits the effective categories break, in words (empty: none). */
export function limitProblems(rows: CategoryRow[]): string[] {
  const out: string[] = [];
  const checkin = rows.filter((r) => r.checkin).length;
  const boarding = rows.filter((r) => r.boarding).length;
  if (checkin > MAX_CHECKIN) out.push(`At most ${MAX_CHECKIN} check-in categories — ${checkin} are marked`);
  if (boarding > MAX_BOARDING) out.push(`At most ${MAX_BOARDING} boarding category — ${boarding} are marked`);
  return out;
}

/** Why turning a limited flag ON is not offered right now (null: it is). `rows` is the
 *  effective set now. Without `stored`, the next one past the limit is locked. With
 *  `stored` (the effective set as stored), the server's no-worse rule: the ceiling is the
 *  larger of the limit and the stored count, so while stored data is over the limit a
 *  check-in can still be swapped for another — never added. */
export function limitLock(
  flag: string,
  on: boolean,
  rows: readonly CategoryRow[],
  stored?: readonly CategoryRow[],
): string | null {
  if (on || (flag !== 'checkin' && flag !== 'boarding')) return null;
  const limit = flag === 'checkin' ? MAX_CHECKIN : MAX_BOARDING;
  const count = (xs: readonly CategoryRow[]) => xs.filter((r) => r[flag]).length;
  const storedCount = stored ? count(stored) : 0;
  if (count(rows) < Math.max(limit, storedCount)) return null;
  const words =
    flag === 'checkin'
      ? `At most ${MAX_CHECKIN} check-in categories`
      : `At most ${MAX_BOARDING} boarding category`;
  return storedCount > limit ? `${words} (${storedCount} stored — swap, don’t add)` : words;
}
